import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import { Urls } from "../../store/urls";

// Lazy imports for CKEditor5 - these will only load when needed
const LazyComponents = {
  CKEditor: null,
  ClassicEditor: null,
  plugins: null,
  MathType: null
};

const loadCKEditorComponents = async () => {
  if (LazyComponents.CKEditor) return LazyComponents;
  
  const [
    { CKEditor },
    {
      ClassicEditor,
      Alignment,
      Autoformat,
      AutoImage,
      Autosave,
      BlockQuote,
      Bold,
      Code,
      Essentials,
      Heading,
      Image, // Add base Image plugin
      ImageBlock,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageToolbar,
      ImageUpload,
      ImageCaption,
      ImageStyle,
      ImageResize,
      ImageTextAlternative,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      Paragraph,
      PasteFromOffice,
      PictureEditing,
      SourceEditing,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      Underline,
      WordCount,
    },
    MathType
  ] = await Promise.all([
    import("@ckeditor/ckeditor5-react"),
    import("ckeditor5"),
    import("@wiris/mathtype-ckeditor5/dist/index.js")
  ]);

  // Load CSS
  await import("ckeditor5/ckeditor5.css");

  LazyComponents.CKEditor = CKEditor;
  LazyComponents.ClassicEditor = ClassicEditor;
  LazyComponents.plugins = {
    Alignment, Autoformat, AutoImage, Autosave, BlockQuote, Bold, Code,
    Essentials, Heading, Image, ImageBlock, ImageInline, ImageInsert, ImageInsertViaUrl,
    ImageToolbar, ImageUpload, ImageCaption, ImageStyle, ImageResize,
    ImageTextAlternative, Indent, IndentBlock, Italic, Link, LinkImage,
    List, ListProperties, MediaEmbed, Mention, Paragraph, PasteFromOffice,
    PictureEditing, SourceEditing, Strikethrough, Subscript, Superscript,
    Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties,
    TableToolbar, TextTransformation, TodoList, Underline, WordCount
  };
  LazyComponents.MathType = MathType.default || MathType;

  return LazyComponents;
};

// Your existing upload adapter code
const uploadImage = (data) => {
  return axios({
    method: "POST",
    url: Urls.POST_APPS_ARTICLE_IMAGE_API,
    data: data,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

class ImageUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file.then(
      (file) => {
        return new Promise((resolve, reject) => {
          const data = new FormData();
          data.append("image", file);
          uploadImage(data)
            .then((response) => {
              resolve({
                default: response.data.data.image_url,
              });
            })
            .catch((err) => {
              reject(err);
            });
        });
      }
    );
  }

  abort() {}
}

function CustomUploadAdapterPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new ImageUploadAdapter(loader);
  };
}

const CustomEditor5 = (props) => {
  const editorRef = useRef(null);
  const editorWordCountRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [editorComponents, setEditorComponents] = useState(null);

  const LICENSE_KEY = process.env.REACT_APP_CKEDITOR_LICENSE_KEY || 'GPL';

  // Intersection Observer to detect when editor container is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible && !isLoading) {
            setIsVisible(true);
          }
        });
      },
      {
        rootMargin: '100px', // Load 100px before it comes into view
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [isVisible, isLoading]);

  // Load CKEditor components when visible
  useEffect(() => {
    if (isVisible && !editorComponents && !isLoading) {
      setIsLoading(true);
      loadCKEditorComponents()
        .then((components) => {
          setEditorComponents(components);
          setIsLayoutReady(true);
        })
        .catch((error) => {
          console.error('Failed to load CKEditor components:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isVisible, editorComponents, isLoading]);

  // Handle pasted content
  useEffect(() => {
    if (props?.isPastedToStory && editorRef?.current) {
      if (props?.initData) {
        editorRef?.current?.data?.set(props?.initData);
      }
    }
  }, [props?.initData, props?.isPastedToStory, editorRef?.current]);

  // undo, redo, mediaEmbed
  const { editorConfig } = useMemo(() => {
    if (!isLayoutReady || !editorComponents) return {};
    
    const { plugins } = editorComponents;

    return {
      editorConfig: {
        toolbar: {
          items: [
            "sourceEditing", "|", "heading", "|",
            "bold", "italic", "underline", "strikethrough", "subscript", "superscript", "|",
            "link", "imageUpload", "insertTable", "blockQuote", "|",
            "alignment", "|", "bulletedList", "numberedList", "todoList", "outdent", "indent", "|",
            "MathType", "ChemType",
          ],
          shouldNotGroupWhenFull: true,
        },
        plugins: [
          // Core plugins first
          plugins.Essentials,
          plugins.Paragraph,
          plugins.Heading,
          plugins.Bold,
          plugins.Italic,
          plugins.Underline,
          plugins.Strikethrough,
          plugins.Subscript,
          plugins.Superscript,
          plugins.Code,
          
          // Image plugins - order matters!
          plugins.Image, // Base image plugin MUST come first
          plugins.ImageBlock,
          plugins.ImageInline,
          plugins.ImageInsert,
          plugins.ImageInsertViaUrl,
          plugins.ImageToolbar,
          plugins.ImageUpload,
          plugins.ImageCaption,
          plugins.ImageStyle,
          plugins.ImageResize,
          plugins.ImageTextAlternative,
          plugins.PictureEditing,
          plugins.AutoImage,
          
          // Other plugins
          plugins.Link,
          plugins.LinkImage,
          plugins.List,
          plugins.ListProperties,
          plugins.TodoList,
          plugins.Indent,
          plugins.IndentBlock,
          plugins.Alignment,
          plugins.BlockQuote,
          plugins.Table,
          plugins.TableCaption,
          plugins.TableCellProperties,
          plugins.TableColumnResize,
          plugins.TableProperties,
          plugins.TableToolbar,
          plugins.MediaEmbed,
          plugins.Mention,
          plugins.PasteFromOffice,
          plugins.SourceEditing,
          plugins.TextTransformation,
          plugins.WordCount,
          plugins.Autoformat,
          plugins.Autosave,
          
          // Custom plugins
          editorComponents.MathType,
        ],
        image: {
          toolbar: [
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'resizeImage'
          ]
        },
        extraPlugins: [CustomUploadAdapterPlugin],
        placeholder: "Type or paste your content here!",
        licenseKey: LICENSE_KEY,
      },
    };
  }, [isLayoutReady, editorComponents]);

  return (
    <div className="ckeditor5_wrapper" ref={containerRef}>
      <div
        className="editor-container editor-container_classic-editor editor-container_include-word-count"
        // style={{ minHeight: '300px' }} // Prevent layout shift
      >
        {!isVisible && (
          <div className="editor-placeholder" style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '4px'
          }}>
            <p>Scroll down to load the editor...</p>
          </div>
        )}

        {isVisible && isLoading && (
          <div className="editor-loading" style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '4px'
          }}>
            <p>Loading editor...</p>
          </div>
        )}

        {isVisible && !isLoading && editorComponents && editorConfig && (
          <>
            <div className="editor-container__editor">
              <editorComponents.CKEditor
                editor={editorComponents.ClassicEditor}
                config={editorConfig}
                onReady={(editor) => {
                  editorRef.current = editor;
                  if (props.showWordCount) {
                    const wordCount = editor.plugins.get("WordCount");
                    if (editorWordCountRef.current) {
                      editorWordCountRef.current.appendChild(
                        wordCount.wordCountContainer
                      );
                    }
                  }
                }}
                onChange={(event, editor) => {
                  const data = editor?.data?.get();
                  if (props?.onChange) props.onChange(data);
                }}
                data={props?.initData}
                onBlur={props?.onBlur}
                onError={(err) => console.error("CKEditor Error:", err)}
              />
            </div>
            <div
              className="editor_container__word-count"
              ref={editorWordCountRef}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CustomEditor5;